import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import StreamingMarkdown from './StreamingMarkdown';
import { getAiChatVars } from './themes';
import { AiChatVariantContext } from './context';
import { AiChatProps } from './types';
import * as S from './styles';

// 좌우 이중 꺾쇠(닫기/뒤로가기) 아이콘 — currentColor 로 테마 대응.
// 헤더 closeIcon 기본값은 없앴으므로, 이 꺾쇠를 쓰려는 소비자가 직접 import 해 공급한다.
export const ChevronIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12.75 17.25L17.25 12L12.75 6.75"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.75 17.25L11.25 12L6.75 6.75"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * 공유 AI Chat 프레젠테이션 컴포넌트.
 * 회의록(라이트)·파일 뷰어(다크)가 동일 UI 를 공유한다. 데이터/테마/헤더/액션은 props 로 주입.
 * 색상은 CSS 변수(themes.ts)로 Root 에 주입해 variant 별로 스위치한다.
 */
const AiChatView = ({
  variant,
  data,
  header,
  greeting,
  disclaimer,
  placeholder,
  suggestions = [],
  disabled = false,
  noticeText,
  sourceChangedText = '음성파일이 추가되었어요',
  emptyIcon,
  renderAvatar,
  renderActions,
  isMobile,
  className,
}: AiChatProps) => {
  const {
    messages,
    isSending,
    isInitializing = false,
    onSend,
    onStop,
    loadMore,
    hasMore = false,
    isLoadingMore = false,
  } = data;

  const [text, setText] = useState('');

  const bodyRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // 과거 로드(prepend) 직전 scrollHeight — null 이면 append(하단 스크롤)
  const prevScrollHeightRef = useRef<number | null>(null);

  const hasMessages = messages.length > 0;

  // 스트리밍(타이핑) 중인 답변 id 집합 — 이 동안 "생성중..." 유지 + 액션 버튼 숨김
  const [typingIds, setTypingIds] = useState<Set<string>>(() => new Set());
  const startTyping = useCallback((id: string) => {
    setTypingIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  const endTyping = useCallback((id: string) => {
    setTypingIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // 타이핑으로 답변 높이가 늘어나는 동안, 사용자가 하단 근처에 있으면 계속 하단 고정
  const scrollToBottomIfNear = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  // 메시지 변경 시: prepend(과거 로드)면 스크롤 위치 보존, append(전송/수신)면 하단으로
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (prevScrollHeightRef.current != null) {
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = null;
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // 위로 스크롤 시 과거 메시지 로드
  const handleScroll = () => {
    const el = bodyRef.current;
    if (!el || !loadMore || !hasMore || isLoadingMore) return;
    if (el.scrollTop <= 40) {
      prevScrollHeightRef.current = el.scrollHeight;
      loadMore();
    }
  };

  // textarea 자동 높이
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 100)}px`;
  }, [text]);

  const canSend = !disabled && !isSending && text.trim().length > 0;
  const canStop = isSending && !!onStop;

  const handleSend = () => {
    if (disabled || isSending) return;
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText('');
  };

  const handleChip = (chip: string) => {
    if (disabled || isSending) return;
    onSend(chip);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !(e.nativeEvent as any).isComposing
    ) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSendButton = () => {
    if (isSending) {
      onStop?.();
      return;
    }
    handleSend();
  };

  const messageNodes = messages.map((m) => {
    if (m.kind === 'source_changed') {
      return <S.SourceChanged key={m.id}>{sourceChangedText}</S.SourceChanged>;
    }
    if (m.kind === 'user') {
      return (
        <S.UserWrap key={m.id} isAnimated={m.isAnimated}>
          <S.UserBubble>
            <S.UserText>{m.text}</S.UserText>
          </S.UserBubble>
        </S.UserWrap>
      );
    }
    // assistant
    // 방금 받은 답변만 타이핑 애니메이션(이력은 즉시 표시)
    const animate = Boolean(m.isAnimated);
    const isTyping = typingIds.has(m.id);
    // 생성 중이거나 아직 타이핑 중이면 "진행" 상태로 취급
    const isStreaming = m.status === 'pending' || isTyping;
    return (
      <S.AiGroup key={m.id} isAnimated={m.isAnimated}>
        <S.AiHead>
          <S.AvatarSlot>{renderAvatar?.(isStreaming)}</S.AvatarSlot>
          {isStreaming && <S.AiStatus>생성중...</S.AiStatus>}
          {m.status === 'done' && !isTyping && (
            <S.AiStatus isDone>생성완료</S.AiStatus>
          )}
        </S.AiHead>
        {m.status === 'error' ? (
          <S.AiText isMobile={isMobile}>
            답변 생성에 실패했어요. 다시 시도해 주세요.
          </S.AiText>
        ) : (
          m.status === 'done' && (
            <S.AiText isMobile={isMobile}>
              <StreamingMarkdown
                text={m.text}
                animate={animate}
                onStart={() => startTyping(m.id)}
                onProgress={scrollToBottomIfNear}
                onDone={() => endTyping(m.id)}
              />
            </S.AiText>
          )
        )}
        {m.status === 'done' && !isTyping && renderActions && (
          <>
            <S.MsgDivider />
            <S.MsgActions>{renderActions(m)}</S.MsgActions>
          </>
        )}
      </S.AiGroup>
    );
  });

  return (
    <AiChatVariantContext.Provider value={variant}>
      <S.Root
        className={className}
        isMobile={isMobile}
        style={getAiChatVars(variant)}
      >
        {header.variant === 'mobile' ? (
          <S.MobileHeader>
            <S.BackButton
              type="button"
              onClick={header.onClose}
              aria-label="뒤로가기"
            >
              {header.closeIcon}
            </S.BackButton>
            <S.MobileTitle>
              {header.titleIcon && (
                <S.TitleIcon>{header.titleIcon}</S.TitleIcon>
              )}
              {header.title}
            </S.MobileTitle>
            {header.onReset && (
              <S.ResetButton
                type="button"
                onClick={header.onReset}
                disabled={header.resetDisabled}
              >
                채팅 초기화
              </S.ResetButton>
            )}
          </S.MobileHeader>
        ) : (
          <S.Header>
            <S.TitleWrap>
              {header.titleIcon && (
                <S.TitleIcon>{header.titleIcon}</S.TitleIcon>
              )}
              <S.Title>{header.title}</S.Title>
            </S.TitleWrap>
            {header.onReset && (
              <S.ResetButton
                type="button"
                onClick={header.onReset}
                disabled={header.resetDisabled}
              >
                채팅 초기화
              </S.ResetButton>
            )}
            {header.onClose && (
              <>
                <S.HeaderDivider />
                <S.CloseButton
                  type="button"
                  onClick={header.onClose}
                  aria-label="닫기"
                >
                  {header.closeIcon}
                </S.CloseButton>
              </>
            )}
          </S.Header>
        )}

        <S.Body ref={bodyRef} onScroll={handleScroll}>
          {isInitializing ? (
            <S.Loading>
              <Spinner theme="ai" size="32px" />
            </S.Loading>
          ) : hasMessages ? (
            <S.ChatBody>
              {isLoadingMore && (
                <S.LoadMore>이전 대화를 불러오는 중…</S.LoadMore>
              )}
              {messageNodes}
            </S.ChatBody>
          ) : (
            <S.Empty>
              {emptyIcon}
              <S.Greeting>
                <S.GreetingSub>{greeting.sub}</S.GreetingSub>
                <S.GreetingMain>{greeting.main}</S.GreetingMain>
              </S.Greeting>
            </S.Empty>
          )}
        </S.Body>

        <S.Footer isMobile={isMobile}>
          {!isInitializing && !hasMessages && suggestions.length > 0 && (
            <S.Chips>
              {suggestions.map((chip) => (
                <S.Chip
                  key={chip}
                  type="button"
                  disabled={disabled || isSending}
                  onClick={() => handleChip(chip)}
                >
                  {chip}
                </S.Chip>
              ))}
            </S.Chips>
          )}

          <S.InputCard isDisabled={disabled}>
            {disabled && noticeText && (
              <S.InputNotice>{noticeText}</S.InputNotice>
            )}
            <S.InputWrap
              hasContent={text.trim().length > 0}
              isSending={isSending}
              isDisabled={disabled}
            >
              <S.Input
                ref={taRef}
                isMobile={isMobile}
                rows={1}
                value={text}
                disabled={disabled}
                placeholder={placeholder}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <S.Send
                type="button"
                active={canSend || canStop}
                isMobile={isMobile}
                onClick={handleSendButton}
                aria-label={canStop ? '중단' : '전송'}
              >
                {canStop ? (
                  <S.StopSquare isMobile={isMobile} />
                ) : (
                  <svg
                    width={isMobile ? 24 : 20}
                    height={isMobile ? 24 : 20}
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M15.98 8.03L7.35 16.63"
                      stroke="white"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.65 16.65L16.65 7.35L7.35 7.35"
                      stroke="white"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </S.Send>
            </S.InputWrap>
          </S.InputCard>

          <S.Disclaimer>{disclaimer}</S.Disclaimer>
        </S.Footer>
      </S.Root>
    </AiChatVariantContext.Provider>
  );
};

export default AiChatView;

export { default as AiChatActionButton } from './ActionButton';
export type {
  AiChatData,
  AiChatMessage,
  AiChatAssistantMessage,
  AiChatVariant,
} from './types';
